<template>
  <div class="page-container">
    <Navbar />

    <main class="page-content">
      <div class="page-header animate-fade-up">
        <Breadcrumbs :items="[{ label: 'Support' }]" />
        <h1 class="page-title">Customer Support</h1>
        <p class="page-subtitle">Get help from our team or browse common questions</p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Contact Information -->
        <div class="lg:col-span-1 space-y-8">
          <BaseCard class="animate-fade-up stagger-1">
            <div class="p-6">
              <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-6 flex items-center gap-2">
                <div class="w-8 h-8 rounded-xl bg-accent-500/10 dark:bg-accent-500/15 flex items-center justify-center">
                  <PhoneIcon class="h-4 w-4 text-accent-600 dark:text-accent-400" />
                </div>
                Contact Information
              </h2>

              <div class="space-y-5">
                <div class="flex items-start space-x-3 group">
                  <EnvelopeIcon class="h-5 w-5 text-accent-500 dark:text-accent-400 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-200" />
                  <div>
                    <p class="text-sm font-medium text-surface-900 dark:text-surface-100">Email</p>
                    <a href="mailto:support@neutralfuels.net" class="text-sm text-accent-600 dark:text-accent-400 hover:underline font-mono">
                      support@neutralfuels.net
                    </a>
                  </div>
                </div>

                <div class="flex items-start space-x-3 group">
                  <PhoneIcon class="h-5 w-5 text-accent-500 dark:text-accent-400 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-200" />
                  <div>
                    <p class="text-sm font-medium text-surface-900 dark:text-surface-100">Phone</p>
                    <a href="tel:+97142345678" class="text-sm text-accent-600 dark:text-accent-400 hover:underline font-mono">
                      +971 4 234 5678
                    </a>
                  </div>
                </div>

                <div class="flex items-start space-x-3 group">
                  <MapPinIcon class="h-5 w-5 text-accent-500 dark:text-accent-400 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-200" />
                  <div>
                    <p class="text-sm font-medium text-surface-900 dark:text-surface-100">Address</p>
                    <p class="text-sm text-surface-500 dark:text-surface-400">
                      Suite 415, Liberty House<br />
                      DIFC, Dubai, UAE
                    </p>
                  </div>
                </div>

                <div class="flex items-start space-x-3 group">
                  <ClockIcon class="h-5 w-5 text-accent-500 dark:text-accent-400 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-200" />
                  <div>
                    <p class="text-sm font-medium text-surface-900 dark:text-surface-100">Business Hours</p>
                    <p class="text-sm text-surface-500 dark:text-surface-400">
                      Sunday - Thursday<br />
                      <span class="font-mono text-xs">9:00 AM - 6:00 PM (GST)</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </BaseCard>

          <!-- Quick Links -->
          <BaseCard class="animate-fade-up stagger-2">
            <div class="p-6">
              <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-5 flex items-center gap-2">
                <div class="w-8 h-8 rounded-xl bg-primary-500/10 dark:bg-primary-500/15 flex items-center justify-center">
                  <svg class="h-4 w-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                  </svg>
                </div>
                Quick Links
              </h2>

              <div class="space-y-1">
                <router-link
                  to="/terms"
                  class="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-surface-600 dark:text-surface-400 hover:bg-surface-50 dark:hover:bg-surface-800/60 hover:text-accent-600 dark:hover:text-accent-400 transition-all duration-200"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-accent-500/40"></span>
                  Terms and Conditions
                </router-link>
                <router-link
                  to="/privacy"
                  class="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-surface-600 dark:text-surface-400 hover:bg-surface-50 dark:hover:bg-surface-800/60 hover:text-accent-600 dark:hover:text-accent-400 transition-all duration-200"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-accent-500/40"></span>
                  Privacy Policy
                </router-link>
                <router-link
                  to="/cookies"
                  class="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-surface-600 dark:text-surface-400 hover:bg-surface-50 dark:hover:bg-surface-800/60 hover:text-accent-600 dark:hover:text-accent-400 transition-all duration-200"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-accent-500/40"></span>
                  Cookie Policy
                </router-link>
              </div>
            </div>
          </BaseCard>
        </div>

        <!-- Support Form -->
        <div class="lg:col-span-2 space-y-8">
          <BaseCard class="animate-fade-up stagger-2">
            <div class="p-6">
              <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-6 flex items-center gap-2">
                <div class="w-8 h-8 rounded-xl bg-accent-500/10 dark:bg-accent-500/15 flex items-center justify-center">
                  <PaperAirplaneIcon class="h-4 w-4 text-accent-600 dark:text-accent-400" />
                </div>
                Send us a message
              </h2>

              <form @submit.prevent="submitForm" class="space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="label">Your Name *</label>
                    <input
                      v-model="form.name"
                      type="text"
                      required
                      class="input"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label class="label">Your Email *</label>
                    <input
                      v-model="form.email"
                      type="email"
                      required
                      class="input"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label class="label">Subject *</label>
                  <input
                    v-model="form.subject"
                    type="text"
                    required
                    class="input"
                    placeholder="How can we help you?"
                  />
                </div>

                <div>
                  <label class="label">Priority</label>
                  <select v-model="form.priority" class="input">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label class="label">Message *</label>
                  <textarea
                    v-model="form.message"
                    required
                    rows="6"
                    class="input"
                    placeholder="Please describe your issue or question in detail..."
                  ></textarea>
                </div>

                <transition
                  enter-active-class="transition duration-300 ease-out"
                  enter-from-class="opacity-0 -translate-y-2"
                  enter-to-class="opacity-100 translate-y-0"
                >
                  <div v-if="successMessage" class="rounded-2xl border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-900/20 backdrop-blur-sm p-4 flex items-start space-x-3">
                    <CheckCircleIcon class="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <p class="text-sm text-emerald-800 dark:text-emerald-200">{{ successMessage }}</p>
                  </div>
                </transition>

                <transition
                  enter-active-class="transition duration-300 ease-out"
                  enter-from-class="opacity-0 -translate-y-2"
                  enter-to-class="opacity-100 translate-y-0"
                >
                  <div v-if="errorMessage" class="rounded-2xl border border-red-200/80 dark:border-red-800/60 bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm p-4 flex items-start space-x-3">
                    <ExclamationCircleIcon class="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p class="text-sm text-red-800 dark:text-red-200">{{ errorMessage }}</p>
                  </div>
                </transition>

                <div class="flex justify-end pt-4">
                  <BaseButton
                    type="submit"
                    variant="primary"
                    size="lg"
                    :loading="isSubmitting"
                    :icon="PaperAirplaneIcon"
                  >
                    {{ isSubmitting ? 'Sending...' : 'Send Message' }}
                  </BaseButton>
                </div>
              </form>
            </div>
          </BaseCard>

          <!-- FAQ Section -->
          <BaseCard class="animate-fade-up stagger-3">
            <div class="p-6">
              <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-6 flex items-center gap-2">
                <div class="w-8 h-8 rounded-xl bg-amber-500/10 dark:bg-amber-500/15 flex items-center justify-center">
                  <svg class="h-4 w-4 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
                  </svg>
                </div>
                Frequently Asked Questions
              </h2>

              <div class="space-y-6">
                <div class="group">
                  <h3 class="font-semibold text-surface-900 dark:text-surface-100 mb-2 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors duration-200">
                    How do I reset my password?
                  </h3>
                  <p class="text-sm text-surface-500 dark:text-surface-400 leading-relaxed">
                    Click on "Forgot Password" on the login page, enter your email, and follow the instructions sent to your inbox.
                  </p>
                </div>

                <div class="border-t border-surface-200/60 dark:border-surface-800/60"></div>

                <div class="group">
                  <h3 class="font-semibold text-surface-900 dark:text-surface-100 mb-2 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors duration-200">
                    How do I export my transaction data?
                  </h3>
                  <p class="text-sm text-surface-500 dark:text-surface-400 leading-relaxed">
                    Navigate to your Dashboard, apply any filters you need, and click the "Export to CSV" button at the top of the transaction table.
                  </p>
                </div>

                <div class="border-t border-surface-200/60 dark:border-surface-800/60"></div>

                <div class="group">
                  <h3 class="font-semibold text-surface-900 dark:text-surface-100 mb-2 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors duration-200">
                    Who can I contact for billing questions?
                  </h3>
                  <p class="text-sm text-surface-500 dark:text-surface-400 leading-relaxed">
                    Please email <span class="font-mono text-xs text-accent-600 dark:text-accent-400">accounts@neutralfuels.net</span> or call our finance team at <span class="font-mono text-xs">+971 4 234 5679</span>.
                  </p>
                </div>

                <div class="border-t border-surface-200/60 dark:border-surface-800/60"></div>

                <div class="group">
                  <h3 class="font-semibold text-surface-900 dark:text-surface-100 mb-2 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors duration-200">
                    How often is my data updated?
                  </h3>
                  <p class="text-sm text-surface-500 dark:text-surface-400 leading-relaxed">
                    Transaction data is updated in real-time. You can refresh your dashboard at any time to see the latest information.
                  </p>
                </div>
              </div>
            </div>
          </BaseCard>
        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useToast } from '@/composables/useToast'

import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'

import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const { success } = useToast()

const userProfile = computed(() => store.state.userProfile)
const isSubmitting = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const form = ref({
  name: '',
  email: '',
  subject: '',
  priority: 'medium',
  message: ''
})

const submitForm = async () => {
  isSubmitting.value = true
  successMessage.value = ''
  errorMessage.value = ''

  try {
    // Simulate API call (replace with actual API endpoint)
    await new Promise((resolve) => setTimeout(resolve, 1500))

    // In a real implementation, you would send this to your backend
    console.log('Support form submitted:', {
      ...form.value,
      userEmail: userProfile.value?.email,
      timestamp: new Date().toISOString()
    })

    successMessage.value = 'Your message has been sent successfully! We\'ll get back to you within 24 hours.'
    success('Support request submitted successfully!')

    // Reset form
    form.value = {
      name: '',
      email: '',
      subject: '',
      priority: 'medium',
      message: ''
    }
  } catch (error) {
    console.error('Error submitting support form:', error)
    errorMessage.value = 'Failed to send your message. Please try again or contact us directly.'
  } finally {
    isSubmitting.value = false
  }
}
</script>
